import { useEffect, useMemo, useRef } from 'react';
import { atom, bind, reatomAsync, sleep, throwIfAborted, withAbort, withDataAtom, withErrorAtom } from '@reatom/framework';
import { useAction, useAtom, useCtx } from '@reatom/npm-react';
import { z } from 'zod';
import { isApiError } from '@/js/libs/api/index.ts';
import { getComponentDebugName } from '@/js/libs/reatom-devtools/index.ts';
import { TOP_CTX_REF, UNMOUNT } from './constants.js';
import { parseError } from './parseError.js';
const defaultParameters = [];
const useRequestLegacy = (config) => {
    const { 
    // Api method
    apiMethod, 
    // parameter, which will be passed to apiMethod
    parameters = defaultParameters, 
    // Default data on inits
    defaultData, active = true, debounce = undefined, onSuccess = () => { }, onFail = () => { }, mapData = (v) => v, subscriptions = {}, } = config;
    const isMetaNeeded = subscriptions.all ?? true;
    const isErrorNeeded = subscriptions.error ?? isMetaNeeded;
    const isPendingNeeded = subscriptions.pending ?? isMetaNeeded;
    const isStatusNeeded = subscriptions.status ?? isMetaNeeded;
    const reactCtx = useCtx();
    const configRef = useRef({
        active,
        debounce,
        defaultData,
        mapData,
        parameters,
        onSuccess,
        onFail,
    });
    configRef.current = {
        active,
        debounce,
        defaultData,
        mapData,
        parameters,
        onSuccess,
        onFail,
    };
    const name = getComponentDebugName(apiMethod.name);
    const model = useMemo(() => {
        const statusAtom = atom(undefined, `${name}._statusAtom`);
        const fetchAction = reatomAsync(async (ctx, ...params) => {
            const cfg = configRef.current;
            if (cfg.debounce)
                await sleep(cfg.debounce);
            throwIfAborted(ctx.controller);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const allParams = {};
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { paramsOrder } = apiMethod;
            for (let i = 0; i < paramsOrder.length; i += 1) {
                switch (paramsOrder[i].type) {
                    case 'body':
                        allParams[paramsOrder[i].paramName] = params[i];
                        break;
                    case 'formData':
                        for (const [k, v] of params[i].entries()) {
                            allParams[k] = v;
                        }
                        break;
                    case 'parameters':
                        Object.assign(allParams, params[i]);
                        break;
                    default:
                        throw new Error('no defined params place');
                }
            }
            const response = await apiMethod({ params: allParams, signal: ctx.controller.signal });
            // fix for the backward compatibility
            response.data = response.data.data;
            if (typeof response.data === 'object') {
                response.data.adnz_response_code = response.status;
            }
            // call `mapData` and `onSuccess` here to handle errors in the catch.
            const mappedData = cfg.mapData(response.data);
            try {
                TOP_CTX_REF.ctx = ctx;
                // @ts-expect-error params correct
                cfg.onSuccess(response.data, params, response.status, fetch);
            }
            finally {
                TOP_CTX_REF.ctx = null;
            }
            return Object.assign(response, { mappedData });
        }, {
            name,
            onEffect: (ctx) => statusAtom(ctx, undefined),
            onFulfill: (ctx, { status }) => statusAtom(ctx, status),
            onReject: (ctx, error) => statusAtom(ctx, isApiError(error, z.any()) ? error.status : undefined),
        }).pipe(withAbort(), withDataAtom(configRef.current.defaultData, (ctx, response) => response.mappedData), withErrorAtom((ctx, e) => {
            const error = parseError(e, name);
            // safety call `onFail` before render
            if (error)
                ctx.schedule(() => configRef.current.onFail(error));
            return error;
        }));
        fetchAction.pendingAtom.__reatom.name = fetchAction.pendingAtom.__reatom.name?.replace('pendingAtom', '_pendingAtom');
        const isLoadingAtom = atom((ctx, state) => {
            const isLoading = ctx.spy(fetchAction.pendingAtom) > 0;
            if (state === undefined)
                return isPendingNeeded && active;
            return isLoading;
        }, `${name}.isLoadingAtom`);
        const setData = bind(reactCtx, fetchAction.dataAtom);
        const metaAtom = atom((ctx) => ({
            pending: isPendingNeeded ? ctx.spy(isLoadingAtom) : ctx.get(isLoadingAtom),
            error: isErrorNeeded ? ctx.spy(fetchAction.errorAtom) : ctx.get(fetchAction.errorAtom),
            status: isStatusNeeded ? ctx.spy(statusAtom) : ctx.get(statusAtom),
            setData,
        }), `${name}._metaAtom`);
        const unmount = bind(reactCtx, fetchAction.abort);
        return {
            ...fetchAction,
            statusAtom,
            metaAtom,
            unmount,
            fetch: fetchAction,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiMethod, bind]);
    const { unmount } = model;
    const [data] = useAtom(model.dataAtom);
    const [meta] = useAtom(model.metaAtom);
    const fetch = useAction((ctx) => model.fetch(TOP_CTX_REF.ctx ?? ctx, ...configRef.current.parameters));
    useEffect(() => {
        if (active)
            fetch().catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, fetch, JSON.stringify(parameters), apiMethod]);
    useEffect(() => () => unmount(UNMOUNT), [unmount]);
    return [data, meta, fetch];
};
export default useRequestLegacy;
